/**
 * Barra flotante: acciones sobre nodos seleccionados en el mapa (borrado masivo, mover a carpeta)
 * @module components/map/MapBulkActions
 */

import { useState } from 'react'
import { Trash2, FolderInput, X } from 'lucide-react'
import { useUIStore, useNodeStore, useFiberStore, useFolderStore, useAuthStore } from '@/stores'
import { hasPermission } from '@/utils/permissions'
import { authService } from '@/services/authService'

const DELETE_CONCURRENCY = 4

async function runWithConcurrency<T>(items: T[], limit: number, fn: (item: T) => Promise<void>) {
  let i = 0
  const workers = Array.from({ length: limit }, async () => {
    while (i < items.length) {
      const idx = i++
      await fn(items[idx])
    }
  })
  await Promise.all(workers)
}

export function MapBulkActions() {
  const user = useAuthStore((s) => s.user)
  const impOk = user?.role !== 'SUPER_ADMIN' || !!authService.getImpersonateSlug()
  const canWrite =
    impOk && hasPermission(user?.role, 'topology:write', user?.permissions)

  const selectedNodeIds = useUIStore((s) => s.selectedNodeIds)
  const clearMapSelection = useUIStore((s) => s.clearMapSelection)
  const addNotification = useUIStore((s) => s.addNotification)

  const deleteNode = useNodeStore((s) => s.deleteNode)
  const loadNodes = useNodeStore((s) => s.loadNodes)
  const fiberInit = useFiberStore((s) => s.init)
  const folders = useFolderStore((s) => s.folders)
  const assignNodesToFolder = useFolderStore((s) => s.assignNodesToFolder)

  const [targetFolderId, setTargetFolderId] = useState('')
  const [working, setWorking] = useState(false)

  if (!canWrite || selectedNodeIds.length === 0) return null

  const handleDelete = async () => {
    const n = selectedNodeIds.length
    const ok = window.confirm(
      `¿Eliminar ${n} nodo(s) seleccionado(s)?\n\n` +
        'Se eliminarán también los cables conectados a esos nodos (cascada en base de datos). Esta acción no se puede deshacer desde aquí.'
    )
    if (!ok) return

    setWorking(true)
    const errors: string[] = []
    await runWithConcurrency(selectedNodeIds, DELETE_CONCURRENCY, async (id) => {
      try {
        await deleteNode(id)
      } catch (e) {
        errors.push(`${id.slice(0, 8)}…: ${e instanceof Error ? e.message : 'error'}`)
      }
    })
    clearMapSelection()
    await loadNodes()
    await fiberInit()
    setWorking(false)
    if (errors.length) {
      addNotification('warning', `Eliminados con incidencias: ${errors.length}. ${errors.slice(0, 3).join(' ')}`)
    } else {
      addNotification('success', `${n} nodo(s) eliminado(s)`)
    }
  }

  const handleMoveToFolder = async () => {
    if (!targetFolderId) {
      addNotification('warning', 'Elige una carpeta')
      return
    }
    setWorking(true)
    try {
      const updated = await assignNodesToFolder(targetFolderId, selectedNodeIds)
      clearMapSelection()
      await loadNodes()
      await fiberInit()
      addNotification('success', `${updated} nodo(s) asignados a la carpeta`)
    } catch (e) {
      addNotification('error', e instanceof Error ? e.message : 'Error al mover')
    } finally {
      setWorking(false)
    }
  }

  return (
    <div
      className="pointer-events-auto absolute bottom-20 left-1/2 z-[1200] flex max-w-[min(96vw,520px)] -translate-x-1/2 flex-col gap-2 rounded-xl border border-amber-200 bg-amber-50/95 px-3 py-2 shadow-xl dark:border-amber-800 dark:bg-amber-950/90 sm:flex-row sm:items-center"
      role="region"
      aria-label="Acciones de selección múltiple"
    >
      <span className="text-sm font-medium text-amber-950 dark:text-amber-100 whitespace-nowrap">
        {selectedNodeIds.length} seleccionado(s)
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="max-w-[200px] rounded-lg border border-amber-300 bg-white px-2 py-1.5 text-xs dark:border-amber-700 dark:bg-dark-800"
          value={targetFolderId}
          onChange={(e) => setTargetFolderId(e.target.value)}
          aria-label="Carpeta destino"
        >
          <option value="">Carpeta…</option>
          {folders.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={working || !targetFolderId}
          onClick={() => void handleMoveToFolder()}
          className="inline-flex items-center gap-1 rounded-lg bg-amber-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
        >
          <FolderInput className="h-3.5 w-3.5" />
          Mover
        </button>
        <button
          type="button"
          disabled={working}
          onClick={() => void handleDelete()}
          className="inline-flex items-center gap-1 rounded-lg border border-red-300 bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-800 hover:bg-red-100 dark:border-red-800 dark:bg-red-950/50 dark:text-red-200"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Eliminar
        </button>
        <button
          type="button"
          disabled={working}
          onClick={() => clearMapSelection()}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1.5 text-xs dark:border-dark-600"
          title="Quitar selección"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
